<script>
  import { onMount } from "svelte";
  import api from "../../services/api.js";

  let documents = [];
  let loading = true;
  let error = null;
  let downloadingId = null;

  // Filter states
  let startDate = "";
  let endDate = "";
  let documentType = "";

  // Document type options
  const documentTypes = [
    { value: "", label: "All Types" },
    { value: "account_application", label: "Account Application" },
    { value: "account_statement", label: "Account Statements" },
    { value: "trade_confirmation", label: "Trade Confirmations" },
    { value: "tax_document", label: "Tax Documents" },
    { value: "tax_1099", label: "Tax Form 1099" },
    { value: "prospectus", label: "Prospectus" },
    { value: "other", label: "Other" },
  ];

  onMount(async () => {
    await loadDocuments();
  });

  async function loadDocuments() {
    loading = true;
    error = null;
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (documentType) params.document_type = documentType;

      const response = await api.getDocuments(params);
      documents = response.documents || [];
    } catch (err) {
      error = err.message;
      documents = [];
    } finally {
      loading = false;
    }
  }

  async function handleFilter() {
    await loadDocuments();
  }

  async function handleClearFilters() {
    startDate = "";
    endDate = "";
    documentType = "";
    await loadDocuments();
  }

  async function handleDownload(doc) {
    downloadingId = doc.id;
    try {
      // Use the download endpoint which returns a file
      const token = api.getToken();
      const response = await fetch(`${api.baseUrl}/api/documents/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || `${doc.type}_${doc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      error = `Failed to download: ${err.message}`;
    } finally {
      downloadingId = null;
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  }

  function getDocumentTypeLabel(type) {
    const typeMap = {
      account_application: "Account Application",
      account_statement: "Account Statement",
      trade_confirmation: "Trade Confirmation",
      tax_document: "Tax Document",
      tax_1099: "Tax Form 1099",
      tax_1099_b: "Tax Form 1099-B",
      tax_1099_div: "Tax Form 1099-DIV",
      tax_1099_int: "Tax Form 1099-INT",
      prospectus: "Prospectus",
      monthly_statement: "Monthly Statement",
      annual_statement: "Annual Statement",
    };
    return typeMap[type] || type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Document";
  }

  function getDocumentIcon(type) {
    if (type?.includes("tax") || type?.includes("1099")) {
      return "fa-file-invoice-dollar";
    } else if (type?.includes("statement")) {
      return "fa-file-alt";
    } else if (type?.includes("confirmation")) {
      return "fa-file-check";
    } else if (type?.includes("prospectus")) {
      return "fa-book";
    }
    return "fa-file-pdf";
  }
</script>

<header class="mb-6 flex flex-wrap items-start justify-between gap-4">
  <div>
    <h1 class="text-2xl font-bold text-ink">Documents</h1>
    <p class="text-sm text-ink-secondary mt-1">Statements, trade confirmations, and tax forms for your account.</p>
  </div>
  <button
    class="btn btn-outline btn-sm"
    on:click={loadDocuments}
    disabled={loading}
  >
    <i class="fas fa-sync-alt text-ink-tertiary" class:fa-spin={loading}></i>
    Refresh
  </button>
</header>

<!-- Filters -->
<div class="card card-pad mb-6">
  <h2 class="text-lg font-semibold text-ink mb-4">Filter documents</h2>
  <div class="flex flex-wrap gap-4">
    <div class="flex-1 min-w-[200px]">
      <label class="label" for="filter_start_date">
        Start date
      </label>
      <input
        type="date"
        id="filter_start_date"
        bind:value={startDate}
        class="input"
      />
    </div>
    <div class="flex-1 min-w-[200px]">
      <label class="label" for="filter_end_date">
        End date
      </label>
      <input
        type="date"
        id="filter_end_date"
        bind:value={endDate}
        class="input"
      />
    </div>
    <div class="flex-1 min-w-[200px]">
      <label class="label" for="filter_document_type">
        Document type
      </label>
      <select
        id="filter_document_type"
        bind:value={documentType}
        class="select"
      >
        {#each documentTypes as type}
          <option value={type.value}>{type.label}</option>
        {/each}
      </select>
    </div>
    <div class="flex items-end gap-2">
      <button
        class="btn btn-primary"
        on:click={handleFilter}
      >
        Apply
      </button>
      <button
        class="btn btn-ghost"
        on:click={handleClearFilters}
      >
        Clear
      </button>
    </div>
  </div>
</div>

<!-- Error message -->
{#if error}
  <div class="banner banner-danger mb-6">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {error}
  </div>
{/if}

<!-- Loading state -->
{#if loading}
  <div class="card overflow-hidden">
    <div class="divide-y divide-line">
      <div class="flex items-center gap-4 px-6 py-4">
        <div class="skeleton w-10 h-10 rounded-xl"></div>
        <div class="flex-1 space-y-2">
          <div class="skeleton h-4 w-1/3"></div>
          <div class="skeleton h-3 w-1/4"></div>
        </div>
      </div>
      <div class="flex items-center gap-4 px-6 py-4">
        <div class="skeleton w-10 h-10 rounded-xl"></div>
        <div class="flex-1 space-y-2">
          <div class="skeleton h-4 w-1/2"></div>
          <div class="skeleton h-3 w-1/4"></div>
        </div>
      </div>
      <div class="flex items-center gap-4 px-6 py-4">
        <div class="skeleton w-10 h-10 rounded-xl"></div>
        <div class="flex-1 space-y-2">
          <div class="skeleton h-4 w-2/5"></div>
          <div class="skeleton h-3 w-1/4"></div>
        </div>
      </div>
    </div>
  </div>
{:else if documents.length === 0}
  <!-- Empty state -->
  <div class="card card-pad text-center py-12">
    <i class="fas fa-folder-open text-4xl text-ink-tertiary mb-4"></i>
    <h3 class="text-lg font-semibold text-ink mb-1">No documents found</h3>
    <p class="text-sm text-ink-secondary">
      {#if startDate || endDate || documentType}
        No documents match your filter criteria. Try adjusting your filters.
      {:else}
        Your account documents will appear here once available.
        <br />
        Documents include account statements, trade confirmations, and tax forms.
      {/if}
    </p>
  </div>
{:else}
  <!-- Documents table -->
  <div class="card overflow-hidden">
    <div class="block w-full overflow-x-auto">
      <table class="w-full border-collapse">
        <thead>
          <tr>
            <th class="th">Document</th>
            <th class="th">Type</th>
            <th class="th">Date</th>
            <th class="th text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each documents as doc}
            <tr class="hover:bg-canvas">
              <td class="td">
                <div class="flex items-center gap-3">
                  <div class="shrink-0 w-10 h-10 rounded-xl bg-surface-variant flex items-center justify-center">
                    <i class="fas {getDocumentIcon(doc.type)} text-ink-secondary"></i>
                  </div>
                  <span class="text-sm font-medium text-ink">
                    {doc.name || getDocumentTypeLabel(doc.type)}
                  </span>
                </div>
              </td>
              <td class="td">
                <span class="badge badge-neutral">
                  {getDocumentTypeLabel(doc.type)}
                  {#if doc.sub_type}
                    <span class="text-ink-tertiary">({doc.sub_type})</span>
                  {/if}
                </span>
              </td>
              <td class="td text-ink-secondary">
                {formatDate(doc.date || doc.created_at)}
              </td>
              <td class="td text-right">
                <button
                  class="btn btn-ghost btn-sm"
                  on:click={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                >
                  {#if downloadingId === doc.id}
                    <i class="fas fa-spinner fa-spin"></i>
                    Downloading...
                  {:else}
                    <i class="fas fa-download text-ink-tertiary"></i>
                    Download
                  {/if}
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </div>

  <!-- Results summary -->
  <div class="mt-4 text-sm text-ink-secondary">
    Showing {documents.length} document{documents.length !== 1 ? 's' : ''}
  </div>
{/if}

<!-- Info section -->
<div class="banner banner-info mt-8">
  <h6 class="font-semibold mb-2">
    <i class="fas fa-info-circle mr-2"></i>
    About documents
  </h6>
  <ul class="text-sm list-disc list-inside space-y-1">
    <li><strong>Account Statements</strong> &mdash; Monthly summaries of your account activity</li>
    <li><strong>Trade Confirmations</strong> &mdash; Records of each executed trade</li>
    <li><strong>Tax Documents (1099)</strong> &mdash; Annual tax forms for reporting investment income</li>
    <li><strong>Prospectus</strong> &mdash; Official documents describing investment options</li>
  </ul>
</div>
